import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';

import {OrdnerEnum} from '../../core/model/ordner.enum';
import {NachrichtSummary} from '../../core/model/nachricht/nachricht';
import {NachrichtActionButtonProvider} from '../../core/services/nachricht-action-button.provider';
import {handleStepUp} from '../../shared/utils/oiam.handler';
import {environment} from '../../../environments/environment';
import {getPostfachFormat} from '../../shared/utils/date-time.helper';
import {NachrichtenFilter} from "../../core/model/nachrichten-filter";
import {NachrichtAction} from "../../shared/components/nachricht-action-buttons/nachricht-action.enum";
import {
    NachrichtActionModalsComponent
} from "../../shared/components/nachricht-action-modals/nachricht-action-modals.component";
import {NachrichtListItemComponent} from "../../shared/components/nachricht-list-item/nachricht-list-item.component";
import {HinweisboxComponent} from "../../shared/components/hinweisbox/hinweisbox.component";
import {KokosI18NextPipe} from "../../shared/pipes/kokos-i18next/kokos-i18next.pipe";
import {HinweisboxTypeEnum} from "../../shared/components/hinweisbox/hinweisbox-type.enum";
import {UserAccountService} from "../../core/services/user-account.service";
import {vNiveauEnum} from "../../core/model/nachricht/vniveau.enum";
import {VertretungsBeziehungEnum} from "../../core/model/nachricht/vertretungs-beziehung.enum";
import {PersonDisplayService} from "../../core/services/person-display.service";
import {
    I18nRerenderOnLanguageChangeDirective
} from "../../shared/directives/i18n-rerender-on-language-change.directive";


@Component({
    selector: 'kokos-postfach-nachricht-list',
    imports: [NachrichtListItemComponent, NachrichtActionModalsComponent, HinweisboxComponent, KokosI18NextPipe, I18nRerenderOnLanguageChangeDirective],
    providers: [NachrichtActionButtonProvider],
    templateUrl: './postfach-nachricht-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class PostfachNachrichtListComponent {

    @Input() nachrichtFolder = OrdnerEnum.ERHALTEN;
    @Input() nachrichten: NachrichtSummary[] = [];
    @Input() onlineKommunikation = true;
    @Input() selectedFilter = NachrichtenFilter.ALL // not used for now
    @Output() reload = new EventEmitter<OrdnerEnum>();

    @ViewChild('nachrichtActionModals', {static: false}) nachrichtActionModals: NachrichtActionModalsComponent;

    private readonly nachrichtActionButtonProvider = inject(NachrichtActionButtonProvider);
    protected readonly userAccountService = inject(UserAccountService);
    private readonly personDisplayService = inject(PersonDisplayService);

    protected readonly getPostfachFormat = getPostfachFormat;
    protected readonly VertretungsBeziehungEnum = VertretungsBeziehungEnum;
    protected readonly HinweisboxTypeEnum = HinweisboxTypeEnum;

    onNachrichtActionButtonClicked(action: NachrichtAction, nachricht: NachrichtSummary): void {
        this.nachrichtActionModals.handleAction(action, nachricht);
    }

    getSpecificClassList(nachricht: NachrichtSummary): string {
        switch (this.nachrichtFolder) {
            case OrdnerEnum.ERHALTEN:
                return (this.showNachrichtActions(nachricht) ? 'ba-icon-application-for-funds' : 'ba-icon-envelope ba-message-unread');
            case OrdnerEnum.ENTWURF:
                return 'ba-icon-apply';
            case OrdnerEnum.GELOESCHT:
            case OrdnerEnum.GESENDET:
                return 'ba-icon-application-for-funds';
        }
    }

    getVNiveauAusreichend(nachricht: NachrichtSummary): boolean {
        return nachricht.vNiveauAusreichend !== false;
    }


    showNachrichtActions(nachricht: NachrichtSummary): boolean {
        return nachricht.gelesen && this.getVNiveauAusreichend(nachricht);
    }

    getNachrichtAction(ordner: OrdnerEnum, nachricht: NachrichtSummary): NachrichtAction[] {
        return this.nachrichtActionButtonProvider.getButtons(ordner, nachricht, this.onlineKommunikation);
    }

    onStepUpUpClicked(nachricht: NachrichtSummary): void {
        if (environment.local) {
            return;
        }
        this.callHandleStepUp(nachricht.vNiveau);
    }

    protected callHandleStepUp(vNiveau: vNiveauEnum): void {
        handleStepUp(vNiveau);
    }

    getErstleserIfNotSelf(leserName: string, leserId: string): string {
        if (!leserId || this.userAccountService.isSelf(leserId)) return null;
        return this.personDisplayService.displayName(leserName, leserId);
    }

    getAutorIfVertreter(nachricht: NachrichtSummary) {
        if (nachricht.vertretungsBeziehung !== VertretungsBeziehungEnum.PERSON_VERTRETUNG) return null;
        return this.personDisplayService.displayName(nachricht.autorName, nachricht.autorId);
    }
}
